import { v4 as uuidv4 } from 'uuid';
import React, { useCallback } from 'react';
import HotelsTab from './components/HotelsTab/HotelsTab';
import StayAreasTab from './components/StayAreasTab/StayAreasTab';
import { BookingsTab } from './components/BookingsTab/BookingsTab';
import { useHistory, useLocation } from '@react-router';
import { AutopopulateAccommodationPlacesScenario } from '@graphql/generated-base';
import { AdminAccommodationsTelemetryProvider } from './Accommodations.telemetry';
import { AccommodationsProvider } from './context/Accommodations.provider';
import { useAccommodationsContext } from './context/useAccommodationsContext';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import { getValidCoordinates, isValidCoordinates } from '@shared/core/places';
import { AccommodationsTourProvider } from './components/AccommodationsTour';
import { EventVenueDialog } from './components/EventVenueDialog';
import { StayDatesDialog, useStayDatesDialog } from './components/StayDatesDialog';
import { useVenueInfo } from './components/VenueInfo';
import { useAutoPopulationWithDataRefetch } from './hooks/useAutoPopulationWithDataRefetch';
import { useAccommodationsSetup } from './hooks/useAccommodationsSetup';
import { useStayAreasAutoPopulation } from '@shared/utils/stayAreas/useStayAreasAutoPopulation';
import { runAutoPopulationTasks } from '@shared/utils/autoPopulation/runAutoPopulationTasks';
import AccommodationsTabs from './components/AccommodationsTabs/AccommodationsTabs';
import AccommodationsHeader from './components/AccommodationsHeader/AccommodationsHeader';
import AccommodationsRoot from './components/AccommodationsRoot/AccommodationsRoot';
import type { TabId } from './components/AccommodationsTabs/AccommodationsTabs.types';
import type { AdvancedLocationInputV2FormFields } from '@shared/components/LocationInputV2';
import type { AccommodationsComponentProps } from './Accommodations.types';
import { useRegisterDialogIds } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_DIALOGS } from './Accommodations.dialogs';

// Only include dialogs that always mount regardless of active tab.
// accommodationTourTooltip and aiRecommendationsFeedbackDialog are in HotelsTab,
// stayAreasTooltip is in StayAreasTab — they register their own IDs when they mount.
const DIALOG_IDS: readonly string[] = [ACCOMMODATIONS_DIALOGS.VENUE_DIALOG, ACCOMMODATIONS_DIALOGS.DATES_DIALOG];

const AccommodationsInner = React.memo<AccommodationsComponentProps>(({ componentData, ...props }) => {
  useAccommodationsSetup({ componentData, ...props });
  const { pageSlug } = useAccommodationsContext();
  const history = useHistory();
  const location = useLocation();

  const activeTab: TabId = location.pathname.endsWith('/guests') ? 'bookings' : location.pathname.endsWith('/stay-areas') ? 'stayAreas' : 'hotels';

  const handleTabChange = useCallback(
    (tab: TabId) => {
      const basePath = location.pathname.replace(/\/(stay-areas|guests)$/, '');
      const suffix = tab === 'stayAreas' ? '/stay-areas' : tab === 'bookings' ? '/guests' : '';
      history.replace(`${basePath}${suffix}`);
    },
    [history, location.pathname],
  );

  const venueInfoProps = useVenueInfo();
  const { handleEditVenue, handleAddVenue, eventVenueState, isSuperAdmin, isOpen: isVenueDialogOpen, onClose: handleVenueDialogClose } = venueInfoProps;

  const { isOpen: isDatesDialogOpen, onClose: handleDatesDialogClose, requestOpen: requestDatesDialogOpen } = useStayDatesDialog();

  const handleEditDates = useEventCallback(() => {
    requestDatesDialogOpen();
  });

  const { eventInfo } = useEventInfo();

  const { startAutoPopulation: startAccommodationsAutoPopulation } = useAutoPopulationWithDataRefetch({
    mode: 'auto',
    context: 'EventVenue',
  });

  const { startAutoPopulation: startStayAreasAutoPopulation } = useStayAreasAutoPopulation({
    mode: 'start-only',
    context: 'EventVenue',
  });

  const handleOnSaveVenue = useEventCallback(async (location: AdvancedLocationInputV2FormFields) => {
    const eventId = eventInfo?.eventId;
    if (!eventId) return;

    await runAutoPopulationTasks('EventVenue', eventId, [
      {
        operation: 'accommodations',
        enabled: isValidCoordinates(location),
        run: () =>
          startAccommodationsAutoPopulation({
            venue: { latitude: location.latitude, longitude: location.longitude, googlePlaceId: location.placeId },
            eventId,
            scenario: AutopopulateAccommodationPlacesScenario.venueChange,
            idempotentId: `${uuidv4()}:venueChange`,
          }),
      },
      {
        operation: 'stayAreas',
        enabled: true,
        run: () =>
          startStayAreasAutoPopulation({
            venue: getValidCoordinates(location),
            eventId,
            scenario: 'venueChange',
          }),
      },
    ]);
  });

  const tabContent = (() => {
    switch (activeTab) {
      case 'hotels':
        return <HotelsTab />;
      case 'stayAreas':
        return <StayAreasTab />;
      case 'bookings':
        return <BookingsTab />;
    }
  })();

  return (
    <AccommodationsTourProvider>
      <AccommodationsRoot
        tabs={
          <AccommodationsTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        }
        header={
          activeTab === 'hotels' ? (
            <AccommodationsHeader
              onEditVenue={handleEditVenue}
              onAddVenue={handleAddVenue}
              onEditDates={handleEditDates}
            />
          ) : null
        }
        content={tabContent}
        dialogs={
          <>
            <EventVenueDialog
              eventVenueState={eventVenueState}
              isOpen={isVenueDialogOpen()}
              onClose={handleVenueDialogClose}
              pageSlug={pageSlug}
              isSuperAdmin={isSuperAdmin}
              onSaveVenue={handleOnSaveVenue}
            />
            <StayDatesDialog
              isOpen={isDatesDialogOpen()}
              onClose={handleDatesDialogClose}
            />
          </>
        }
      />
    </AccommodationsTourProvider>
  );
});

AccommodationsInner.displayName = 'AccommodationsInner';

const Accommodations: React.FC<AccommodationsComponentProps> = ({ componentData, ...props }) => {
  useRegisterDialogIds(DIALOG_IDS);
  return (
    <AdminAccommodationsTelemetryProvider>
      <AccommodationsProvider
        componentData={componentData}
        pageConfig={props.pageConfig}
        containerId={props.containerId}
        pageSlug={props.pageSlug}
        eventHandle={props.eventHandle}
      >
        <AccommodationsInner
          componentData={componentData}
          {...props}
        />
      </AccommodationsProvider>
    </AdminAccommodationsTelemetryProvider>
  );
};

export default Accommodations;
